import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import { Package, Search } from 'lucide-react';
import StatusChip from '../common/StatusChip';

/**
 * `variant="pos"` é a variante escopada da tela Nova Venda (POS —
 * DESIGN-SYSTEM.md §2.4/§3.4, campo de busca do `prototipo-telas-insercao.html`
 * ~L1120-1131): caixa com borda tracejada (sólida + cor de marca no focus),
 * sem label flutuante (usa `placeholder`), e lista de resultados no padrão
 * `.pos-combo-list` (ícone/avatar + nome + preço, borda, sombra, max-height
 * com scroll). O variant default (sem prop) permanece 100% inalterado para
 * as demais telas que usam este componente (BudgetItems) — não altere o
 * visual padrão fora deste branch condicional. Usado com `variant="pos"`
 * pelos carrinhos de `service_order/Operator.js` e `service_quotes/Operator.js`
 * (Operação da OS/Operação do orçamento).
 */
const DefaultAutocomplete = ({
        key,
        handleGetSelected,
        id,
        label,
        placeholder,
        handleChange,
        options,
        controlDisabled,
        autoFocus,
        inputProps,
        sx,
        onKeyDown,
        variant
    }) => {
    const isPos = variant === 'pos';
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [open, setOpen] = React.useState(false);

    const onChange = (e) => {
        setInputValue(e.target.value);
        setLoading(true);
        
        // Simula um delay para mostrar o loading
        setTimeout(() => {
            handleChange(e);
            setLoading(false);
        }, 500);
    }

    const onSelect = (value) => {
        handleGetSelected(value);
        // Clear input value after selection and prevent re-opening
        setTimeout(() => {
            setInputValue('');
        }, 0);
        setOpen(false);
    }    

    return (
        <>
            <Autocomplete
                sx={{
                    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                        display: inputValue ? undefined : 'none',
                    },
                    ...(isPos && {
                        '& .MuiOutlinedInput-root': {
                            height: 52,
                            borderRadius: '12px',
                            paddingLeft: '14px',
                            paddingRight: '14px',
                            backgroundColor: 'transparent',
                        },
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2,
                            borderStyle: 'dashed',
                            borderRadius: '12px',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: (theme) => theme.palette.divider,
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderStyle: 'solid',
                            borderColor: (theme) => theme.palette.primary.main,
                            borderWidth: 2,
                        },
                        '& .MuiOutlinedInput-input': {
                            fontSize: 14,
                        },
                    }),
                    ...sx,
                }}
                size='small'
                key={key}
                freeSolo
                open={open}
                onOpen={() => {
                    if (inputValue) {
                        setOpen(true);
                    }
                }}
                onClose={() => setOpen(false)}
                loading={loading}
                loadingText="Carregando..."
                //noOptionsText="Nenhum registro encontrado"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            borderRadius: '12px',
                            boxShadow: (theme) => theme.customShadows?.md,
                            ...(isPos && {
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                maxHeight: 280,
                            }),
                        },
                    },
                }}
                renderOption={(props, option) =>
                    isPos ? (
                        <li
                            {...props}
                            key={option.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '10px 12px',
                                fontSize: 13,
                            }}
                        >
                            <div
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 8,
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.info.bg,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {option.image ? (
                                    <img src={option.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <Package size={16} strokeWidth={1.8} />
                                )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{option.name}</div>
                                    {/* Pill produto/serviço (§4 `itemType`) — só aparece quando a opção
                                        carrega `type` (busca unificada de OS/Orçamento); em `Sale.js`
                                        (só produtos) o campo não existe e o pill fica oculto. */}
                                    {option.type && (
                                        <StatusChip domain="itemType" value={option.type === 'product' ? 'produto' : 'servico'} />
                                    )}
                                </div>
                            </div>
                            {option.value !== undefined && (
                                <div style={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: 12.5 }}>{`R$ ${option.value}`}</div>
                            )}
                        </li>
                    ) : (
                        <li {...props} key={option.id}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                    gap: 12,
                                    padding: '6px 8px',
                                }}
                            >
                                <span style={{ fontSize: 13.5 }}>{option.name}</span>
                                {option.value !== undefined && (
                                    <span style={{ whiteSpace: 'nowrap', fontWeight: 700, fontSize: 12.5 }}>{`R$ ${option.value}`}</span>
                                )}
                            </div>
                        </li>
                    )
                }
                onChange={(e, v) => onSelect(v)}
                id={id}
                options={options}
                disabled={controlDisabled}
                inputValue={inputValue}
                onInputChange={(event, value) => {
                    if (!event) {
                        return;
                    }
                    setInputValue(value);
                    setOpen(!!value);
                }}
                clearIcon={inputValue ? undefined : null}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        value={inputValue}
                        autoFocus={autoFocus}
                        disabled={controlDisabled}
                        onChange={onChange}
                        label={isPos ? undefined : label}
                        placeholder={isPos ? placeholder : undefined}
                        variant='outlined'
                        onKeyDown={onKeyDown}
                        InputProps={{
                            ...params.InputProps,
                            ...inputProps,
                            startAdornment: params.InputProps?.startAdornment || (
                                <InputAdornment position="start">
                                    <Search size={16} strokeWidth={2} style={{ opacity: 0.6 }} />
                                </InputAdornment>
                            ),
                        }}
                         />}
            />
        </>
    );
};

export default DefaultAutocomplete;